import { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/components/data-table/data-table';
import { Button } from '@/components/ui/button';
import { DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useScrollOverflow } from '@/hooks/scroll/use-scroll-overflow';
import { useDataTable } from '@/hooks/use-data-table';
import { cn } from '@/lib/utils';
import { useImportEntitiesStore } from '@/modules/onboarding/common/import-entities-modal/state';

export interface ErrorEntitiesContentProps<TError> {
  getErrorEntitiesColumns: () => ColumnDef<TError, any>[];
  backToImportResource: string;
}

export function ErrorEntitiesContent<TError>({
  getErrorEntitiesColumns,
  backToImportResource,
}: ErrorEntitiesContentProps<TError>) {
  const { t } = useTranslation(['authenticated.onboarding.common']);
  const { importErrors, setPreviewErrorOpen } = useImportEntitiesStore();

  // stickyHeader mode creates its own inner scroll container — detect overflow on it
  const { ref: scrollRef, isScrollable } = useScrollOverflow(
    [importErrors.length],
    '[data-scroll-container]'
  );

  const { table } = useDataTable({
    columns: getErrorEntitiesColumns(),
    data: importErrors,
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DialogHeader className={cn('shrink-0 pb-4', isScrollable && 'border-b')}>
        <DialogTitle>{t('importEntities.errorsTitle')}</DialogTitle>
      </DialogHeader>
      <div ref={scrollRef} className="flex min-h-0 flex-1 flex-col py-4">
        <DataTable table={table} stickyHeader />
      </div>
      <div className={cn('flex shrink-0 justify-end gap-2 pt-4', isScrollable && 'border-t')}>
        <Button onClick={() => setPreviewErrorOpen(false)}>{t(backToImportResource)}</Button>
      </div>
    </div>
  );
}
